'use client';
/**
 * Mon planning — la ligne de l'appelant, et rien d'autre (12/08/2026).
 *
 * Ce n'est PAS l'écran « Planning », qui montre tout le laboratoire et reste
 * réservé aux fonctions qui en ont la charge. Ici, chacun voit ses propres
 * affectations et ses propres absences : c'est la question qu'on se pose le
 * matin — « où suis-je cette semaine, et quand suis-je en congé ? » — et elle
 * ne demande le droit de voir le planning de personne d'autre.
 *
 * La route `ged/rh/mon-planning` filtre côté serveur sur l'orgId du porteur du
 * jeton : l'écran ne choisit pas de qui il parle, il ne peut pas.
 *
 * ── Par MOIS d'abord (RQ, 27/08/2026) ──────────────────────────────────────
 *
 * « Je voudrais que ce soit affiché par défaut par mois, et je peux passer au
 * mode par semaine si nécessaire. » L'écran montrait quatre semaines glissantes
 * — une fenêtre qui ne correspond à rien : ni au mois qu'on a en tête, ni à la
 * semaine qu'on tient. On ouvre donc sur le mois courant, et la semaine reste à
 * un bouton pour qui veut le détail des sept jours à venir.
 *
 * ── Un jour sans rien ne dit RIEN (même demande) ───────────────────────────
 *
 * « Mon planning affiche uniquement ce qui est prévu. Si rien n'est annoté,
 * cela s'affiche vide. » Chaque jour portait un tiret. Trente tirets alignés se
 * lisent comme une information — « on m'a mis nulle part » — alors qu'ils ne
 * disent que l'absence de ligne au planning. La case reste donc vide, et seul
 * ce qui est prévu s'écrit.
 */
import { BandeauConventions, PointsConventions } from '@/components/BandeauConventions';
import { useOccurrences, parDate as conventionsParDate } from '@/lib/conventions';
import React from 'react';
import { useGed, lundiDe } from '@/lib/store';
import { I, card, EmptyState, Skeleton, TitrePage, StatePillAbsence } from '@/components/ui';
/* LA COULEUR DU POSTE (RQ, 05/10/2026) — le MÊME point d'entrée que le planning
   mensuel (`pastilleDe`). Une seconde implémentation ferait diverger
   l'apparence d'un écran à l'autre, et c'est précisément ce que la règle de
   `couleurs-personne` interdit. */
import { pastilleDe } from '@/lib/couleurs-personne';
/* LES JOURS FÉRIÉS, écrits comme un événement du calendrier (RQ, 05/10/2026).
   ⚠️ Ils viennent de la réponse de `mon-planning`, pas du magasin : la route
   `ged/rh/feries` est gardée par la section « Planning », que cet écran
   n'exige pas. Même précédent que les postes du planning (04/10/2026). */
import { COULEUR_FERIE, libelleCourtFerie, titreFerie, parDateFerie, feriesEntre, type FerieVue } from '@/lib/feries';
import { EchangesPlanning } from '@/components/screens/EchangesPlanning';
/* LE MODE JOUR (RQ, 05/10/2026) — « quand je clique sur une journée, je veux la
   voir en mode jour ». Le CADRE est partagé, le CONTENU est propre à cet écran :
   ici c'est MA journée, pas celle de tout le monde. */
import { FenetreJour, RubriqueJour } from '@/components/FenetreJour';
import { libelleJourLong } from '@/lib/jour';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { isoJour, aujourdhuiIso } from '@/lib/meta';
type Ligne = {
  documentId: string; date: string; nature: string; statut: string; note?: string;
  /* `couleur` au format `h<teinte>`, ou vide : la teinte est alors CALCULÉE
     depuis le code, par le même module que le planning mensuel. */
  poste: { code: string; libelle: string; couleur?: string | null } | null;
};
/** Une réunion où je suis convié (12/10/2026) — aujourd'hui la revue de direction. */
type Reunion = { nature: string; ref: string; date: string; statut: string; libelle: string };
const COULEUR_REUNION = { fond: 'var(--violet-50)', texte: 'var(--violet-600)', bord: 'var(--violet-600)' };
const titreReunion = (r: Reunion) => `${r.libelle} ${r.ref}${r.statut === 'planifiee' ? '' : r.statut === 'tenue' ? ' (tenue)' : ' (clôturée)'}`;

type AbsenceLigne = {
  documentId: string; dateDebut: string; dateFin: string; motifLabel: string; statut: string;
};

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const fmtJour = (iso: string) => { const [, m, j] = iso.split('-'); return `${j}/${m}`; };

/** Décalage en jours SANS `Date` sur la chaîne : le fuseau du navigateur ne
 *  doit pas faire glisser un lundi sur un dimanche. */
function plusJours(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return isoJour(d);
}

/** Le 1er du mois de `iso`. */
const premierDuMois = (iso: string) => `${iso.slice(0, 7)}-01`;

/** Le mois suivant (ou précédent si `n` est négatif), au 1er. */
function moisDecale(iso: string, n: number): string {
  const [a, m] = iso.split('-').map(Number);
  const t = (a * 12 + (m - 1)) + n;
  return `${String(Math.floor(t / 12)).padStart(4, '0')}-${String((t % 12) + 1).padStart(2, '0')}-01`;
}

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const nomDuMois = (iso: string) => `${MOIS[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;

/**
 * LES NATURES — le MOT, et qui tient le poste.
 *
 * ⚠️ LA COULEUR NE VIENT PLUS D'ICI (RQ, 05/10/2026) : « affiche les postes
 * avec la couleur de chaque poste comme le planning mensuel ». Les cinq natures
 * se partageaient trois teintes, si bien que quatre paillasses tenues dans la
 * semaine sortaient toutes en cyan — l'écran ne disait pas lesquelles. La
 * pastille prend donc la couleur DU POSTE, et la nature reste écrite.
 *
 * `tenue` distingue les natures où l'on TIENT le poste (travail, garde,
 * astreinte) de celles où l'on n'y est pas (formation, repos). Elles gardent le
 * gris : colorer un jour de formation comme la paillasse ferait lire qu'elle a
 * été tenue, et c'est l'inverse qui est vrai.
 */
const NATURES: Record<string, { l: string; tenue: boolean }> = {
  travail: { l: 'Travail', tenue: true },
  garde: { l: 'Garde', tenue: true },
  astreinte: { l: 'Astreinte', tenue: true },
  formation: { l: 'Formation', tenue: false },
  repos: { l: 'Repos', tenue: false },
};

/** Les trois teintes d'une pastille de ligne — celles du poste quand il est
 *  TENU, le gris neutre sinon. Un poste absent (ligne sans poste) reste gris :
 *  on ne colore pas ce qu'on ne sait pas nommer. */
function styleLigne(l: Ligne) {
  const n = NATURES[l.nature] || NATURES.travail;
  if (!n.tenue || !l.poste?.code) {
    return { background: 'var(--gray-25)', color: 'var(--text-muted)', borderColor: 'var(--border-subtle)' };
  }
  return pastilleDe(l.poste.code, l.poste.couleur);
}

export function MonPlanning() {
  const { session } = useGed();
  /* `mois` par défaut — c'est la demande, et c'est aussi l'unité dans laquelle
     un planning se pense (« qui tient la garde en septembre ? »). */
  const [vue, setVue] = React.useState<'mois' | 'semaine'>('mois');
  const [lundi, setLundi] = React.useState(() => lundiDe(new Date()));
  const [lignes, setLignes] = React.useState<Ligne[] | null>(null);
  const [absences, setAbsences] = React.useState<AbsenceLigne[]>([]);
  const [feries, setFeries] = React.useState<FerieVue[]>([]);
  const [reunions, setReunions] = React.useState<Reunion[]>([]);
  /* Le jour ouvert, ou `null`. ⚠️ Il peut sortir de la grille affichée (on
     navigue au jour suivant depuis le 31) : le contenu se lit alors dans ce
     qu'on a en mémoire, et la fenêtre DIT quand elle n'a pas la donnée plutôt
     que de montrer une journée vide qui passerait pour « rien de prévu ». */
  const [jourOuvert, setJourOuvert] = React.useState<string | null>(null);
  const [erreur, setErreur] = React.useState('');

  /* La fenêtre demandée au serveur.
     ⚠️ En vue mois, elle déborde des DEUX côtés : la grille commence au lundi
     qui précède le 1er et finit au dimanche qui suit le dernier jour. Ne lire
     que le mois strict laisserait vides les cases débordantes — un poste tenu
     le 31 août n'apparaîtrait pas sur la grille de septembre où il figure
     pourtant, et l'écran mentirait par omission sur sa propre grille. */
  const debutGrille = vue === 'semaine' ? lundi : lundiDe(new Date(`${premierDuMois(lundi)}T12:00:00`));
  const nbJours = vue === 'semaine' ? 7 : 42;
  const fin = plusJours(debutGrille, nbJours - 1);
  /* Les conventions avec les cliniques, en repère discret (RQ, 04/10/2026) : de
     petits points sous la date, le nom à l'infobulle — comme le planning général. */
  const { occurrences: occConventions } = useOccurrences(debutGrille, fin);
  const conventionsDuJour = React.useMemo(() => conventionsParDate(occConventions), [occConventions]);

  React.useEffect(() => {
    let vivant = true;
  /* ⚠️ FAUX POSITIF ASSUMÉ de `react-hooks/set-state-in-effect` (02/09/2026).
     Le `setState` synchrone ici n'ajuste pas un état à une entrée : il EFFACE
     la donnée précédente avant d'aller en chercher une autre. Sans lui, l'écran
     afficherait les données de la fiche PRÉCÉDENTE pendant tout le temps de la
     requête — un faux vide à l'envers, qui affirme sur la nouvelle fiche ce qui
     était vrai de l'ancienne. C'est le seul cas où l'on veut ce rendu
     intermédiaire, et il ne se dérive pas : la donnée vient du réseau.
     ⚠️ Silence CIBLÉ, ligne à ligne. */
  // eslint-disable-next-line react-hooks/set-state-in-effect
    setLignes(null);
    void fetch(`/api/ged/ged/rh/mon-planning?du=${debutGrille}&au=${fin}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('lecture refusée'))))
      .then((d) => {
        if (!vivant) return;
        setLignes(d.affectations || []); setAbsences(d.absences || []);
        setFeries(d.feries || []); setReunions(d.reunions || []); setErreur('');
      })
      .catch(() => { if (vivant) { setLignes([]); setFeries([]); setReunions([]); setErreur('Planning momentanément illisible — réessayez dans un instant.'); } });
    return () => { vivant = false; };
  }, [debutGrille, fin]);

  /* MES PASSAGES PAR POSTE sur l'année (RQ, 19/08/2026) : la matière de la
     réhabilitation annuelle. La même route que le planning — le périmètre
     « moi seulement » y est déjà gardé côté serveur — appelée une fois sur
     l'année civile courante. */
  const annee = new Date().getFullYear();
  /* Groupé par CODE et non par libellé (05/10/2026) : c'est le code qui porte
     la couleur, et deux postes peuvent partager un intitulé coupé. */
  const [passages, setPassages] = React.useState<{ code: string; poste: string; couleur?: string | null; n: number }[] | null>(null);
  React.useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/rh/mon-planning?du=${annee}-01-01&au=${annee}-12-31`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!vivant || !d) return;
        const compte = new Map<string, { code: string; poste: string; couleur?: string | null; n: number }>();
        for (const a of (d.affectations || []) as { poste?: { code?: string; libelle?: string; couleur?: string | null } | null; posteLibelle?: string; nature?: string }[]) {
          // Le travail et la garde COMPTENT comme passages ; un repos ou une
          // formation ne sont pas une tenue de poste.
          if (a.nature && !['travail', 'garde', 'astreinte'].includes(a.nature)) continue;
          const code = a.poste?.code || '';
          const nom = a.poste?.libelle || a.posteLibelle || code;
          if (!nom) continue;
          const cle = code || nom;
          const v = compte.get(cle);
          if (v) v.n += 1;
          else compte.set(cle, { code, poste: nom, couleur: a.poste?.couleur ?? null, n: 1 });
        }
        setPassages([...compte.values()].sort((x, y) => y.n - x.n));
      })
      .catch(() => { if (vivant) setPassages([]); });
    return () => { vivant = false; };
  }, [annee]);

  const ferieDu = React.useMemo(() => parDateFerie(feries), [feries]);
  const semaines = Array.from({ length: nbJours / 7 }, (_, i) => plusJours(debutGrille, i * 7));
  const absenceDu = (jour: string) => absences.find((a) => a.dateDebut <= jour && a.dateFin >= jour) || null;
  const lignesDu = (jour: string) => (lignes || []).filter((l) => l.date === jour);
  const reunionsDu = (jour: string) => reunions.filter((r) => r.date === jour);
  const aujourdhui = aujourdhuiIso();

  return (
    <section>
      <TitrePage eyebrow="Mon espace" titre="Mon planning"
        sousTitre={<>Vos affectations, vos absences et les revues de direction où vous êtes convié(e). Vous ne voyez ici que votre propre ligne —
          le planning du laboratoire se consulte dans la rubrique « Planning », pour qui en a la
          charge.</>} />

      {/* EN HAUT DU CALENDRIER (RQ, 12/10/2026) — ils étaient sous la grille
          depuis le 04/10 : « mes passages par poste, les afficher en haut ». */}
      {passages !== null && passages.length > 0 && (
        <div style={{ ...card, padding: 14, margin: '14px 0 0' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', marginBottom: 8 }}>
            Mes passages par poste — {annee}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {passages.map((p) => {
              /* LA MÊME COULEUR QUE LA GRILLE (05/10/2026) : le compteur se lit
                 juste au-dessus du calendrier, et deux repères différents pour le
                 même poste obligeraient à relire son nom. */
              const st = p.code
                ? pastilleDe(p.code, p.couleur)
                : { background: 'var(--surface-card)', color: 'var(--text-body)', borderColor: 'var(--border-default)' };
              return (
                <span key={p.code || p.poste} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, background: st.background, color: st.color, border: `1px solid ${st.borderColor}`, borderRadius: 'var(--radius-pill)', padding: '4px 12px', fontSize: 12.5, fontWeight: 600 }}>
                  {p.poste}
                  <strong style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{p.n}</strong>
                </span>
              );
            })}
          </div>
          <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Ces compteurs alimentent la réhabilitation annuelle des postes : le biologiste ou la RH
            les lit pour tout le monde depuis le Planning, avec le seuil retenu par le laboratoire.
          </p>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0', flexWrap: 'wrap' }}>
        <Bouton taille="sm" aria-label={vue === 'mois' ? 'Mois précédent' : 'Semaine précédente'} onClick={() => setLundi(vue === 'mois' ? moisDecale(lundi, -1) : plusJours(lundi, -7))}>
          <I n="chevron-left" s={14} />
        </Bouton>
        <strong style={{ fontSize: 14, minWidth: 168, textAlign: 'center' }}>
          {vue === 'mois' ? nomDuMois(lundi) : `Semaine du ${fmtJour(lundi)}`}
        </strong>
        <Bouton taille="sm" aria-label={vue === 'mois' ? 'Mois suivant' : 'Semaine suivante'} onClick={() => setLundi(vue === 'mois' ? moisDecale(lundi, 1) : plusJours(lundi, 7))}>
          <I n="chevron-right" s={14} />
        </Bouton>
        <Bouton taille="sm" onClick={() => setLundi(lundiDe(new Date()))}>
          {vue === 'mois' ? 'Ce mois-ci' : 'Cette semaine'}
        </Bouton>

        {/* La bascule, à droite du déplacement : on change d'échelle bien moins
            souvent qu'on ne change de période. */}
        <div role="group" aria-label="Échelle d’affichage" style={{ display: 'inline-flex', gap: 6, marginInlineStart: 12 }}>
          <Segment ariaLabel="Vue" valeur={vue} onChange={(valeur) => {
                /* On garde le repère : passer du mois à la semaine ouvaleurre sur la
                   semaine du jour si le mois affiché est le mois courant, et
                   sinon sur sa première semaine — jamais sur une semaine qui
                   n'a rien à valeuroir avaleurec ce qu'on regardait. */
                const auj = aujourdhuiIso();
                if (valeur === 'semaine') setLundi(lundiDe(new Date(`${(auj.slice(0, 7) === lundi.slice(0, 7) ? auj : premierDuMois(lundi))}T12:00:00`)));
                setVue(valeur);
              }} options={(['mois', 'semaine'] as const).map((v) => ({ valeur: v, libelle: v === 'mois' ? 'Mois' : 'Semaine' }))} />
        </div>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', marginInlineStart: 'auto' }}>{session.name}</span>
      </div>

      {erreur && <p role="alert" style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</p>}

      {lignes === null ? (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}><Skeleton rows={5} /></div>
      ) : !lignes.length && !absences.length && !feries.length && !reunions.length ? (
        /* ⚠️ LES FÉRIÉS COMPTENT DANS CETTE CONDITION (05/10/2026). Sans eux, un
           mois sans affectation masquait la grille — et donc le férié qu'on
           venait d'y mettre. Un événement qu'on affiche ne doit pas pouvoir
           être caché par l'absence d'autre chose. */
        <EmptyState titre={vue === 'mois' ? `Rien de planifié en ${nomDuMois(lundi)}` : `Rien de planifié la semaine du ${fmtJour(lundi)}`}
          indice="Vos affectations apparaîtront ici dès que le planning sera établi. Une absence validée s’y affiche aussi." />
      ) : vue === 'mois' ? (
        /* ── GRILLE DU MOIS ────────────────────────────────────────────────
           Sept colonnes, six lignes au plus. Une case sans rien reste VIDE :
           c'est la demande, et c'est aussi ce qu'un calendrier fait
           naturellement — on y lit ce qui est posé, pas la liste de ce qui ne
           l'est pas. */
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {JOURS.map((nom) => (
              <div key={nom} style={{ padding: '8px 10px', background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-default)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)' }}>
                {nom.slice(0, 3)}
              </div>
            ))}
            {semaines.flatMap((debutSem, sem) => JOURS.map((_, i) => {
              const jour = plusJours(debutSem, i);
              /* Les jours débordants sont MONTRÉS, en gris : ils portent de
                 vraies affectations (une garde le 31 du mois précédent), et les
                 masquer ferait disparaître une information exacte. */
              const horsMois = jour.slice(0, 7) !== lundi.slice(0, 7);
              const mesLignes = lignesDu(jour);
              const abs = absenceDu(jour);
              const cejour = jour === aujourdhui;
              return (
                <div key={jour} style={{
                  minHeight: 74, padding: '6px 8px', borderTop: sem ? '1px solid var(--border-subtle)' : 'none',
                  borderInlineStart: i ? '1px solid var(--border-subtle)' : 'none',
                  background: cejour ? 'var(--cyan-50)' : horsMois ? 'var(--gray-25)' : undefined,
                  opacity: horsMois ? 0.6 : 1,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: cejour ? 700 : 600, color: cejour ? 'var(--cyan-700)' : 'var(--text-muted)', marginBottom: 4 }}>
                    {/* ⚠️ LE NUMÉRO EST LE BOUTON, PAS LA CASE (05/10/2026).
                        La case porte déjà les pastilles de poste, qui sont
                        cliquables ailleurs et le deviendront ici : un fond
                        cliquable aurait rendu chaque clic ambigu. Un numéro
                        souligné en pointillés se voit et s'atteint au clavier. */}
                    <button type="button" onClick={() => setJourOuvert(jour)}
                      aria-label={`Voir la journée du ${libelleJourLong(jour)}`}
                      title={`Voir la journée du ${libelleJourLong(jour)}`}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2 }}>
                      {Number(jour.slice(8, 10))}
                    </button>
                    <PointsConventions liste={conventionsDuJour.get(jour)} />
                  </div>
                  {/* ⚠️ LE FÉRIÉ S'AFFICHE QUOI QU'IL ARRIVE — avant l'absence
                      comme avant les postes, et jamais à leur place : le
                      laboratoire travaille les jours fériés, et quelqu'un peut
                      être à la fois en congé et un jour de fête. */}
                  {ferieDu.get(jour) && (
                    <div title={titreFerie(ferieDu.get(jour)!)} aria-label={titreFerie(ferieDu.get(jour)!)}
                      style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-xs)', padding: '1px 5px', marginBottom: 3, background: COULEUR_FERIE.fond, color: COULEUR_FERIE.texte, border: `1px solid ${COULEUR_FERIE.bord}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {libelleCourtFerie(ferieDu.get(jour)!.libelle)}
                    </div>
                  )}
                  {reunionsDu(jour).map((r) => (
                    <div key={r.ref} title={titreReunion(r)} aria-label={titreReunion(r)}
                      style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-xs)', padding: '1px 5px', marginBottom: 3, background: COULEUR_REUNION.fond, color: COULEUR_REUNION.texte, border: `1px solid ${COULEUR_REUNION.bord}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.libelle}
                    </div>
                  ))}
                  {abs ? (
                    <span style={{ display: 'inline-block', fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: 'var(--gray-25)', color: 'var(--text-muted)' }}>
                      {abs.motifLabel}
                    </span>
                  ) : mesLignes.map((l) => {
                    const n = NATURES[l.nature] || NATURES.travail;
                    const st = styleLigne(l);
                    return (
                      <div key={l.documentId} title={l.note || undefined}
                        style={{ fontSize: 11, fontWeight: 600, borderRadius: 'var(--radius-sm)', padding: '2px 6px', marginBottom: 3, background: st.background, color: st.color, border: `1px solid ${st.borderColor}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.poste?.libelle || l.poste?.code || 'Poste inconnu'}
                        {l.nature !== 'travail' && ` · ${n.l}`}
                      </div>
                    );
                  })}
                </div>
              );
            }))}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          <BandeauConventions du={debutGrille} au={fin} occurrences={occConventions} />
          {semaines.map((debutSem) => (
            <div key={debutSem} style={{ ...card, padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '9px 14px', background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-default)', fontSize: 12.5, fontWeight: 700 }}>
                Semaine du {fmtJour(debutSem)}
              </div>
              <div>
                {JOURS.map((nom, i) => {
                  const jour = plusJours(debutSem, i);
                  const mesLignes = lignesDu(jour);
                  const abs = absenceDu(jour);
                  const cejour = jour === aujourdhui;
                  return (
                    <div key={jour} style={{
                      display: 'flex', gap: 12, alignItems: 'center', padding: '8px 14px',
                      borderTop: i ? '1px solid var(--border-subtle)' : 'none',
                      background: cejour ? 'var(--cyan-50)' : undefined,
                    }}>
                      <span style={{ minWidth: 128, fontSize: 12.5, fontWeight: cejour ? 700 : 600, color: cejour ? 'var(--cyan-700)' : 'var(--text-body)' }}>
                        <button type="button" onClick={() => setJourOuvert(jour)}
                          aria-label={`Voir la journée du ${libelleJourLong(jour)}`}
                          title={`Voir la journée du ${libelleJourLong(jour)}`}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2 }}>
                          {nom} {fmtJour(jour)}
                        </button>
                        {' '}<PointsConventions liste={conventionsDuJour.get(jour)} />
                      </span>
                      {ferieDu.get(jour) && (
                        <span title={titreFerie(ferieDu.get(jour)!)} aria-label={titreFerie(ferieDu.get(jour)!)}
                          style={{ fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 10px', background: COULEUR_FERIE.fond, color: COULEUR_FERIE.texte, border: `1px solid ${COULEUR_FERIE.bord}`, whiteSpace: 'nowrap' }}>
                          {libelleCourtFerie(ferieDu.get(jour)!.libelle)}
                        </span>
                      )}
                      {reunionsDu(jour).map((r) => (
                        <span key={r.ref} title={titreReunion(r)}
                          style={{ fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 10px', background: COULEUR_REUNION.fond, color: COULEUR_REUNION.texte, border: `1px solid ${COULEUR_REUNION.bord}`, whiteSpace: 'nowrap' }}>
                          {r.libelle} · {r.ref}
                        </span>
                      ))}
                      {abs ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                          <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--gray-25)', color: 'var(--text-muted)' }}>
                            {abs.motifLabel}
                          </span>
                          <StatePillAbsence statut={abs.statut} />
                        </span>
                      ) : mesLignes.length ? (
                        <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {mesLignes.map((l) => {
                            const n = NATURES[l.nature] || NATURES.travail;
                            const st = styleLigne(l);
                            return (
                              <span key={l.documentId} title={l.note || undefined}
                                style={{ fontSize: 12.5, fontWeight: 600, borderRadius: 'var(--radius-pill)', padding: '3px 10px', background: st.background, color: st.color, border: `1px solid ${st.borderColor}` }}>
                                {l.poste?.libelle || l.poste?.code || 'Poste inconnu'}
                                {l.nature !== 'travail' && ` · ${n.l}`}
                              </span>
                            );
                          })}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {lignes !== null && (!!lignes.length || !!absences.length || !!feries.length || !!reunions.length) && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '10px 0 0', lineHeight: 1.5 }}>
          La couleur d’une pastille est celle <strong>du poste</strong> — la même qu’au planning du
          laboratoire : elle ne change pas d’un mois à l’autre. Une formation ou un repos reste en
          gris : le poste n’y est pas tenu.
          {!!feriesEntre(feries, debutGrille, fin).length && (
            <> Les <strong>jours fériés</strong> sont écrits en ambre. ⚠️ Le laboratoire travaille les
            jours fériés : le repère dit la fête, il ne dit pas que c’est fermé — votre poste reste
            affiché s’il vous est attribué.</>
          )}
          {!!reunions.length && (
            <> Les <strong>revues de direction</strong> auxquelles vous participez sont écrites en violet.</>
          )}
        </p>
      )}

      {/* SOUS LE CALENDRIER (RQ, 04/10/2026) : les échanges. Les passages par
          poste sont remontés au-dessus le 12/10/2026. */}
      <div style={{ marginTop: 14 }}>
      {/* DEMANDER UN ÉCHANGE (RQ, 01/10/2026) — céder ou échanger une affectation
          avec une personne déjà habilitée ; le RH valide, le planning suit. */}
      <EchangesPlanning mode="moi" moiKey={session.key} />


      </div>

      {/* ══ MA JOURNÉE (RQ, 05/10/2026) ═══════════════════════════════════════
          Le cadre est partagé (`FenetreJour`), le contenu est à cet écran : ce
          sont MES postes et MON absence, pas la journée du laboratoire.
          ⚠️ On ne refait AUCUNE requête : la fenêtre lit `lignes`, `absences` et
          `feries` déjà chargés pour la grille. D'où la garde `horsFenetre`
          ci-dessous — en naviguant au jour suivant on peut sortir de ce qui a
          été demandé, et un écran VIDE affirmerait « rien de prévu » alors que
          la bonne phrase est « je n'ai pas cette journée ». */}
      {jourOuvert && (() => {
        const horsFenetre = jourOuvert < debutGrille || jourOuvert > fin;
        const mesLignes = lignesDu(jourOuvert);
        const abs = absenceDu(jourOuvert);
        const f = ferieDu.get(jourOuvert) || null;
        return (
          <FenetreJour date={jourOuvert} onDate={setJourOuvert} onClose={() => setJourOuvert(null)}
            ferie={f} conventions={conventionsDuJour.get(jourOuvert)} eyebrow="Mon planning">
            {horsFenetre ? (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Cette journée est en dehors de la période affichée : elle n’a pas été chargée.
                Fermez la fenêtre et déplacez le calendrier sur {libelleJourLong(jourOuvert, false)}
                {' '}pour la voir.
              </p>
            ) : (
              <>
                {abs && (
                  <RubriqueJour titre="Absence">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <span style={{ fontSize: 11.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--gray-25)', color: 'var(--text-muted)' }}>
                        {abs.motifLabel}
                      </span>
                      <StatePillAbsence statut={abs.statut} />
                    </span>
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                      Du {libelleJourLong(abs.dateDebut, false)} au {libelleJourLong(abs.dateFin, false)}.
                    </span>
                  </RubriqueJour>
                )}

                <RubriqueJour titre={mesLignes.length > 1 ? 'Mes postes ce jour-là' : 'Mon poste ce jour-là'}>
                  {!mesLignes.length ? (
                    /* ⚠️ « Rien de prévu » est une AFFIRMATION, et elle n'est
                       juste que parce qu'on sait avoir chargé cette journée
                       (garde `horsFenetre` ci-dessus). On dit aussi ce que ça
                       ne veut PAS dire : une case vide n'est pas un repos
                       accordé. */
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      Rien ne vous est attribué ce jour-là. Ce n’est pas un repos accordé : c’est
                      l’absence de ligne au planning.
                    </p>
                  ) : mesLignes.map((l) => {
                    const n = NATURES[l.nature] || NATURES.travail;
                    const st = styleLigne(l);
                    const p = l.poste;
                    return (
                      <div key={l.documentId} style={{ display: 'grid', gap: 3, padding: '9px 11px', borderRadius: 'var(--radius-md)', background: st.background, border: `1px solid ${st.borderColor}` }}>
                        <span style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: 13.5, color: st.color }}>
                            {p?.libelle || p?.code || 'Poste inconnu'}
                          </strong>
                          {p?.code && p?.libelle && (
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: st.color, opacity: 0.8 }}>{p.code}</span>
                          )}
                          {l.nature !== 'travail' && (
                            <span style={{ fontSize: 11.5, fontWeight: 700, color: st.color }}>· {n.l}</span>
                          )}
                        </span>
                        {/* La NOTE de l'affectation n'était lisible qu'à
                            l'infobulle dans la grille — une note qu'il faut
                            survoler pour lire n'est pas lue. */}
                        {l.note && (
                          <span style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.45 }}>{l.note}</span>
                        )}
                      </div>
                    );
                  })}
                </RubriqueJour>

                {reunionsDu(jourOuvert).length > 0 && (
                  <RubriqueJour titre="Réunion">
                    {reunionsDu(jourOuvert).map((r) => (
                      <p key={r.ref} style={{ margin: 0, fontSize: 13, color: 'var(--text-body)', lineHeight: 1.5 }}>
                        {titreReunion(r)} — vous y êtes convié(e).
                      </p>
                    ))}
                  </RubriqueJour>
                )}

                {f && (
                  <RubriqueJour titre="Jour férié">
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-body)', lineHeight: 1.5 }}>
                      {f.libelle}
                      {/* ⚠️ Le laboratoire travaille les jours fériés — on le
                          dit, parce que l'intuition contraire est fausse. */}
                      {' — '}le laboratoire n’est pas fermé pour autant
                      {mesLignes.length ? ', et votre poste ci-dessus reste à tenir.' : '.'}
                    </p>
                  </RubriqueJour>
                )}
              </>
            )}
          </FenetreJour>
        );
      })()}
    </section>
  );
}
